import EE from 'events';
import RS from '~/api';
import { KEY_SESSION_STORAGE } from '~/constants';
import extractAnalyticsData from '~/helpers/extractAnalyticsData';
import formatDateWithTimezone from '~/helpers/formatDateWithTimezone';
import getLogTime from '~/helpers/getLogTime';
import pathToString from '~/helpers/pathToString';
import safeStringify from '~/helpers/safeStringify';
import { getFromSessionStorage } from '~/helpers/storage';
import Queue, { IBufferItem, IQueue } from '~/listenerEventService/Queue';
import { clickstreamLogger } from '~/logging';
import { findMatchingRulesForTree } from '~/utils/NodeMatcher';
import settings from '~/utils/Settings';

class EventTransportService extends EE {
  messagesToSend: IQueue;
  // Server start flag
  isWorking = false;
  // Last sent package to AnalyticsEvent webService that is not delivered yet
  deliveringMessage: {
    data: IBufferItem[],
    attemptsLeft: number,
  } = null;
  // Close QueueWebService after sending all messages
  isClosingAfterSending = false;
  // Id of the auto buffer check timer
  checkBufferTimerId: NodeJS.Timeout;
  // The time when cancel overflow timeout starts
  cancelOverflowStartTime = 0;
  // The start time of cancel overflow is set
  isCancelOverflowStartTimeSet = false;
  // Queue overflow flag
  isOverflow = false;

  constructor() {
    super();
    this.messagesToSend = Queue;
  }

  // Attempts of resending message after fail
  get resendAttempts(): number {
    return settings.getOrDefault('RESEND_ATTEMPTS', 5);
  }

  // Time to collect messages before first sending
  get initialTimeout(): number {
    return settings.getOrDefault('INITIAL_TIMEOUT', 1000);
  }

  // Rules for identifying event target
  get nodeMatchingRules() {
    return settings.getOrDefault('ELEMENT_MATCHING_RULES', {});
  }

  // Interval to check buffer again and again if there aren't any income message
  get checkBufferInterval(): number {
    return settings.getOrDefault('CHECK_BUFFER_INTERVAL', 5000);
  }

  // Max allowed amount of messages in queue
  get overflowQueueSize(): number {
    return settings.getOrDefault('OVERFLOW_QUEUE_SIZE', 1000);
  }

  // Amount of messages in queue to exit overflow mode
  get cancelOverflowQueueSize(): number {
    return settings.getOrDefault('CANCEL_OVERFLOW_QUEUE_SIZE', 800);
  }

  // Wait for this timeout before emitting cancel overflow event, if during this timeout queue size
  // becomes over cancelOverflowQueueSize then timeout will reset and cancel overflow event won't be emitted
  get timeToWaitBeforeCancelOverflow(): number {
    return settings.getOrDefault('TIME_TO_WAIT_BEFORE_CANCEL_OVERFLOW', 5000);
  }

  // Event encrypted flag
  get eventEncrypted(): boolean {
    const isCommonEncryptionEnabled = getFromSessionStorage(
      KEY_SESSION_STORAGE,
      'isEncryptEnable'
    );

    let isEncryptionEnabled = false;
    if (isCommonEncryptionEnabled) {
      isEncryptionEnabled = !!(
        settings.getOrDefault('ENABLE_EVENT_ENCRYPTION', 0) &&
        settings.isEnableSettingForLocation('ENABLE_EVENT_ENCRYPTION_REGEX')
      );
    }

    return isEncryptionEnabled;
  }

  initOverflow = () => {
    // Overflow mode wasn't set yet
    if (!this.isOverflow) {
      this.isOverflow = true;

      this.emit('overflow', {
        overflowQueueSize: this.overflowQueueSize,
        queueSize: this.messagesToSend.buffer.length,
      });
    } else {
      if (this.cancelOverflowStartTime !== 0) {
        // Reset the cancel overflow timeout if queue size is over overflowQueueSize during overflow period
        this.cancelOverflowStartTime = 0;
        clickstreamLogger.debug(
          `Reset cancel overflow timeout, time: ${getLogTime()}`
        );
      }
    }
  }

  cancelOverflow = () => {
    // Send cancel-overflow message only after overflow
    if (this.isOverflow) {
      // Start time of cancel overflow timeout is set before
      if (this.isCancelOverflowStartTimeSet) {
        // If cancel overflow timeout finished then emit cancel overflow event
        if (
          Date.now() >
          this.cancelOverflowStartTime + this.timeToWaitBeforeCancelOverflow
        ) {
          clickstreamLogger.debug(
            `Cancel overflow timeout is finished, time ${getLogTime()}`
          );
          // Reset timeout start time
          this.isCancelOverflowStartTimeSet = false;
          this.isOverflow = false;

          this.emit('cancel-overflow', {
            cancelOverflowQueueSize: this.cancelOverflowQueueSize,
            queueSize: this.messagesToSend.buffer.length,
          });
        } else {
          clickstreamLogger.debug(
            `Cancel overflow timeout in process, time: ${getLogTime()}`
          );
        }
      } else {
        // Start time of cancel overflow timeout is not set yet
        // Fix start time of cancel overflow timeout
        this.cancelOverflowStartTime = Date.now();
        this.isCancelOverflowStartTimeSet = true;
        clickstreamLogger.debug(
          `Start cancel overflow timeout, time: ${getLogTime()}`
        );
      }
    }
  }

  resendLastMessage = () => {
    if (this.isWorking && this.deliveringMessage) {
      if (this.deliveringMessage.attemptsLeft > 0) {
        const { data } = this.deliveringMessage;
        this.sendMessage(data);
        this.deliveringMessage.attemptsLeft = this.deliveringMessage.attemptsLeft - 1;
        clickstreamLogger.debug(
          'Resending message: ',
          this.deliveringMessage,
          `attempts left: ${this.deliveringMessage.attemptsLeft}`
        );
      } else {
        // Add delivering message to the beginning of the queue
        const { data } = this.deliveringMessage;
        this.messagesToSend.unshift(data);
        this.deliveringMessage = null;
        this.emit('error', 'Sending message error, no attempts left');
        if (this.isClosingAfterSending) {
          this.isClosingAfterSending = false;
          this.stop();
        } else {
          // Message queue is empty wait for timeout before next check
          this.checkBufferTimerId = setTimeout(
            this.checkBuffer,
            this.checkBufferInterval
          );
        }
      }
    }
  }

  sendMessage = (data: IBufferItem[]) => {
    RS.analyticsEvent
      .set(data, this.eventEncrypted)
      .then((response) => {
        clickstreamLogger.debug(
          'Message sent: ',
          data,
          'Service response: ',
          response
        );
        this.deliveringMessage = null;
        this.checkBuffer();
      })
      .catch((error) => {
        clickstreamLogger.error('Sending message error: ', error, data);
        this.resendLastMessage();
      });
  }

  checkBuffer = () => {
    clickstreamLogger.info('Starting checkBuffer services...');
    if (this.isWorking) {
      if (this.deliveringMessage) {
        return;
      }

      if (this.isClosingAfterSending && this.messagesToSend.buffer.length === 0) {
        this.isClosingAfterSending = false;
        this.stop();

        return;
      }

      const queueLength = this.messagesToSend.buffer.length;

      if (queueLength > this.overflowQueueSize) {
        this.initOverflow();
      } else if (queueLength < this.cancelOverflowQueueSize) {
        this.cancelOverflow();
      }

      if (queueLength > 0) {
        // Remove all messages from the queue before sending and save them into variable
        const messagePackage = this.messagesToSend.getBuffer();
        // Save current sending message
        this.deliveringMessage = {
          data: messagePackage,
          attemptsLeft: this.resendAttempts,
        };
        this.sendMessage(messagePackage);
        clearTimeout(this.checkBufferTimerId);
      } else {
        // Message queue is empty wait for timeout before next check
        this.checkBufferTimerId = setTimeout(
          this.checkBuffer,
          this.checkBufferInterval
        );
      }
    }
  }

  start = () => {
    if (this.isWorking === false) {
      clickstreamLogger.info('Starting messaging services...');
      this.isWorking = true;
      // First check buffer for new incoming messages
      setTimeout(this.checkBuffer, this.initialTimeout);
    } else if (this.isClosingAfterSending === true) {
      this.isClosingAfterSending = false;
      this.checkBuffer();
    }
  }

  stop = () => {
    if (this.isWorking === false) {
      return;
    }
    clickstreamLogger.info('Stopping messaging services...');

    if (this.deliveringMessage || this.messagesToSend.buffer.length > 0) {
      clickstreamLogger.info('Waiting for the last message delivery...');
      this.isClosingAfterSending = true;

      return;
    }

    // Clear array
    this.messagesToSend.clearBuffer();
    this.isWorking = false;
    this.deliveringMessage = null;
    this.isClosingAfterSending = false;
    this.isOverflow = false;

    clickstreamLogger.info('Messaging service stopped.');
  }

  send = (data: any) => {
    if (
      !data ||
      !this.isWorking ||
      this.isClosingAfterSending ||
      data.save === false ||
      !RS.token.userUID
    ) {
      return;
    }
    const eventData = {
      ...data,
      target: undefined,
      processedTarget: undefined,
    };
    if (data.target) {
      for (const [key, value] of Object.entries(
        extractAnalyticsData(data.target)
      )) {
        if (value) {
          eventData[key] = value;
        }
      }
      eventData.pathToTarget = pathToString(data.target);
      if (data.processedTarget) {
        eventData.target = data.processedTarget;
      } else {
        const matching = findMatchingRulesForTree(
          data.target,
          this.nodeMatchingRules
        );
        eventData.target = matching.length
          ? matching.join(', ')
          : eventData.pathToTarget;
      }
    }

    this.messagesToSend.push({
      'eventPath': location.pathname ? location.pathname : '/',
      'eventTime': formatDateWithTimezone(new Date()),
      'eventData': safeStringify(eventData),
      'eventType': data?.type ? data.type : undefined,
      'title': document.title,
      '@type': 'AnalyticsEvent',
    });
  }
}

export default new EventTransportService();
